/**
 * Ranks aggregated Flow groups, assigns colours and creates the two-sided chart series.
 * It centralizes the Top-N/Remaining representation shared by the chart and breakdown projection.
 * Required to update the ranking and Remaining client-side, without another BigQuery query.
 */
import { ChartColorService } from 'src/app/services/chart-color.service';
import type {
    FlowChartViewModel,
    FlowSeriesViewModel,
} from 'src/app/dashboards/common/items/flow-chart.models';
import {
    FlowAggregation,
    FlowBucketAggregate,
} from './flow-dashboard-aggregation';
import { topNWithOther } from 'src/app/utils/top-n-with-other';

import {
    FLOW_IN_PREFIX,
    FLOW_OUT_PREFIX,
    FLOW_REMAINING_COLOR,
    FLOW_REMAINING_LABEL,
} from './flow-dashboard.models';

type FlowDirection = 'in' | 'out';

export interface FlowPresentation {
    currentSeries: FlowSeriesViewModel[];
    inflowBig: string[];
    inflowSmall: string[];
    outflowBig: string[];
    outflowSmall: string[];
    colorIn: Record<string, string>;
    colorOut: Record<string, string>;
    bucketInBig: string[];
    bucketInSmall: string[];
    bucketOutBig: string[];
    bucketOutSmall: string[];
    bucketColorIn: Record<string, string>;
    bucketColorOut: Record<string, string>;
}

export interface FlowPresentationOptions {
    aggregation: FlowAggregation;
    threshold: number;
    hiddenSeries: Set<string>;
    colorMode: 'flow' | 'standard';
    colorLayer: string;
    colorService: ChartColorService;
    zoomStart: number;
    zoomEnd: number;
}

/** Build ranked series and matching chart data from a two-sided Flow aggregate. */
export function buildFlowPresentation(options: FlowPresentationOptions): {
    presentation: FlowPresentation;
    flow: FlowChartViewModel;
} {
    const { aggregation, threshold } = options;
    const rankGroups = (dir: FlowDirection) =>
        topNWithOther(
            Object.keys(aggregation.groups),
            (key) =>
                dir === 'in'
                    ? aggregation.groups[key].inflowTotal
                    : aggregation.groups[key].outflowTotal,
            threshold
        );
    const inflow = rankGroups('in');
    const outflow = rankGroups('out');
    const colorize = (dir: FlowDirection, keys: string[]) => {
        const colors: Record<string, string> = {};
        if (options.colorMode === 'flow') {
            const shades = ChartColorService.getPolarityShades(
                dir === 'in' ? 'positive' : 'negative',
                keys.length
            );
            keys.forEach((key, index) => {
                colors[key] = shades[index];
            });
        } else {
            for (const key of keys) {
                colors[key] = options.colorService.getColor(
                    options.colorLayer,
                    key
                );
            }
        }
        return colors;
    };

    const bucketRanking = (dir: FlowDirection) =>
        topNWithOther(
            aggregation.bucketValues,
            (bucket) =>
                flowBucketTotal(
                    aggregation.byBucket,
                    aggregation.dates,
                    bucket,
                    dir
                ),
            threshold
        );
    const bucketIn = aggregation.groupByBucket
        ? bucketRanking('in')
        : { big: [], small: [] };
    const bucketOut = aggregation.groupByBucket
        ? bucketRanking('out')
        : { big: [], small: [] };

    const presentation: FlowPresentation = {
        currentSeries: [],
        inflowBig: inflow.big,
        inflowSmall: inflow.small,
        outflowBig: outflow.big,
        outflowSmall: outflow.small,
        colorIn: colorize('in', inflow.big),
        colorOut: colorize('out', outflow.big),
        bucketInBig: bucketIn.big,
        bucketInSmall: bucketIn.small,
        bucketOutBig: bucketOut.big,
        bucketOutSmall: bucketOut.small,
        bucketColorIn: colorize('in', bucketIn.big),
        bucketColorOut: colorize('out', bucketOut.big),
    };
    const selected: Record<string, boolean> = {};
    const push = (
        dir: FlowDirection,
        label: string,
        byDate: Record<string, number>,
        color: string
    ) => {
        const name = `${dir === 'in' ? FLOW_IN_PREFIX : FLOW_OUT_PREFIX} ${label}`;
        presentation.currentSeries.push({
            name,
            color,
            dir,
            data: aggregation.dates.map((date) => +(byDate[date] || 0)),
        });
        selected[name] = !options.hiddenSeries.has(name);
    };

    if (aggregation.groupByBucket) {
        appendBucketSeries(
            'in',
            presentation.bucketInBig,
            presentation.bucketInSmall,
            presentation.bucketColorIn
        );
        appendBucketSeries(
            'out',
            presentation.bucketOutBig,
            presentation.bucketOutSmall,
            presentation.bucketColorOut
        );
    } else {
        appendGroupSeries(
            'in',
            presentation.inflowBig,
            presentation.inflowSmall,
            presentation.colorIn
        );
        appendGroupSeries(
            'out',
            presentation.outflowBig,
            presentation.outflowSmall,
            presentation.colorOut
        );
    }

    function appendGroupSeries(
        dir: FlowDirection,
        big: string[],
        small: string[],
        colors: Record<string, string>
    ) {
        for (const key of big) {
            const byDate =
                dir === 'in'
                    ? aggregation.groups[key].inflowByDate
                    : aggregation.groups[key].outflowByDate;
            push(dir, key, byDate, colors[key]);
        }
        if (small.length) {
            push(
                dir,
                FLOW_REMAINING_LABEL,
                flowSumByDate(
                    aggregation.groups,
                    aggregation.dates,
                    small,
                    dir
                ),
                FLOW_REMAINING_COLOR
            );
        }
    }

    function appendBucketSeries(
        dir: FlowDirection,
        big: string[],
        small: string[],
        colors: Record<string, string>
    ) {
        for (const bucket of big) {
            push(
                dir,
                bucket,
                flowBucketByDate(
                    aggregation.byBucket,
                    aggregation.dates,
                    bucket,
                    dir
                ),
                colors[bucket]
            );
        }
        if (small.length) {
            push(
                dir,
                FLOW_REMAINING_LABEL,
                flowSumBucketsByDate(
                    aggregation.byBucket,
                    aggregation.dates,
                    small,
                    dir
                ),
                FLOW_REMAINING_COLOR
            );
        }
    }

    return {
        presentation,
        flow: {
            dates: aggregation.dates,
            series: presentation.currentSeries,
            selected,
            zoomStart: options.zoomStart,
            zoomEnd: options.zoomEnd,
        },
    };
}

export function flowNetByDate(
    dates: string[],
    series: FlowSeriesViewModel[],
    hiddenSeries: Set<string>
): Record<string, number> {
    const net: Record<string, number> = {};
    for (const item of series) {
        if (hiddenSeries.has(item.name)) {
            continue;
        }
        const sign = item.dir === 'in' ? 1 : -1;
        dates.forEach((date, index) => {
            net[date] = (net[date] || 0) + sign * (item.data[index] || 0);
        });
    }
    return net;
}

export function flowSumByDate(
    groups: FlowAggregation['groups'],
    dates: string[],
    keys: string[],
    dir: FlowDirection
): Record<string, number> {
    const sums: Record<string, number> = {};
    for (const key of keys) {
        const byDate =
            dir === 'in' ? groups[key].inflowByDate : groups[key].outflowByDate;
        for (const date of dates) {
            sums[date] = (sums[date] || 0) + (byDate[date] || 0);
        }
    }
    return sums;
}

export function flowBucketByDate(
    byBucket: FlowBucketAggregate,
    dates: string[],
    bucket: string,
    dir: FlowDirection
): Record<string, number> {
    const sums: Record<string, number> = {};
    for (const group of Object.values(byBucket[bucket] ?? {})) {
        const byDate = dir === 'in' ? group.inflowByDate : group.outflowByDate;
        for (const date of dates) {
            sums[date] = (sums[date] || 0) + (byDate[date] || 0);
        }
    }
    return sums;
}

export function flowBucketTotal(
    byBucket: FlowBucketAggregate,
    dates: string[],
    bucket: string,
    dir: FlowDirection
): number {
    return Object.values(flowBucketByDate(byBucket, dates, bucket, dir)).reduce(
        (total, value) => total + value,
        0
    );
}

export function flowSumBucketsByDate(
    byBucket: FlowBucketAggregate,
    dates: string[],
    buckets: string[],
    dir: FlowDirection
): Record<string, number> {
    const sums: Record<string, number> = {};
    for (const bucket of buckets) {
        const byDate = flowBucketByDate(byBucket, dates, bucket, dir);
        for (const date of dates) {
            sums[date] = (sums[date] || 0) + (byDate[date] || 0);
        }
    }
    return sums;
}
